import { Button, DialogRoot, DialogTrigger, TextWithDescription } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { XCircleIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AttributeResponse, useAttributes } from '@/entities/attributes';
import { NestedItemCard } from '@/features/nested-items';
import { AttributeGroupFormValues } from '../AttributeGroupForm/types';
import { AddAttributeDialog } from './AddAttributeDialog';

type Props = {
  disabled?: boolean;
};

export const AttributesList: FC<Props> = ({ disabled }) => {
  const { t } = useTranslation(['attributeGroups', 'common']);
  const { control, setValue, getValues, watch } = useFormContext<AttributeGroupFormValues>();
  const { fields } = useFieldArray({ control, name: 'attributes' });

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: attributesData } = useAttributes({ limit: 1000 });
  const allAttributes = attributesData?.attributes ?? [];

  const currentAttributes = watch('attributes');

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCancelDialog = () => setIsDialogOpen(false);

  // Состав группы — такое же изменение карточки, как правка её поля: без `shouldDirty` кнопка сохранения остаётся неактивной
  const handleSaveDialog = (newAttributes: AttributeResponse[]) => {
    const newCodes = newAttributes.map((a) => ({ code: a.code }));
    setValue('attributes', [...(getValues('attributes') || []), ...newCodes], { shouldDirty: true });
    setIsDialogOpen(false);
  };

  const handleRemoveAttribute = (code: string) => {
    setValue('attributes', getValues('attributes')?.filter((attr) => attr.code !== code) || [], {
      shouldDirty: true,
    });
  };

  const getAttributeData = (code: string): AttributeResponse | undefined => {
    return allAttributes.find((a) => a.code === code);
  };

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-5 items-center">
          <p className="font-medium">{t('attributes')}</p>

          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" onClick={handleOpenDialog}>
                {t('addAttribute')}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {fields.map((field) => {
            const attrData = getAttributeData(field.code);
            if (!attrData) return null;
            return (
              <NestedItemCard
                key={`${attrData.code}.${attrData.parentStructCode}`}
                item={attrData}
                renderTitle={(item) => (
                  <TextWithDescription
                    text={item.name}
                    description={<span className="font-mono text-sm">{item.code}</span>}
                  />
                )}
                renderDescription={(item) => item.description}
                action={
                  !disabled && (
                    <IconButton
                      icon={<XCircleIcon />}
                      color="destructive"
                      label={t('attributeGroups:removeAttributeFromGroup')}
                      onClick={() => handleRemoveAttribute(field.code)}
                    />
                  )
                }
              />
            );
          })}
          {fields.length === 0 && <p className="text-sm text-muted-foreground">{t('noAttributesInGroup')}</p>}
        </div>
      </div>

      <AddAttributeDialog
        existingAttributeCodes={currentAttributes?.map((a) => a.code) || []}
        onCancel={handleCancelDialog}
        onSave={handleSaveDialog}
        className="max-w-160"
      />
    </DialogRoot>
  );
};
